// Olvasottság-mutációk — OPTIMISTA frissítés + rollback (a repo mutation-mintája: `lib/use-entity-mutation.ts`).
//
// Miért NEM a közös `useEntityMutation`: a sor-kattintásos „olvasottnak jelölés" NÉMA művelet (nem kérünk
// siker-toastot minden kattintásra — az `useEntityMutation` mindig ad egyet). A hiba-ág viszont ugyanaz:
// rollback + `sonner` hiba-toast. A „Mind olvasottnak" EXPLICIT user-akció → az kap siker-toastot.
//
// Az optimista írás a feed-PREFIXRE megy (`setQueriesData`), így minden nyitott feed-variáns egyszerre frissül;
// a snapshotot ugyanerről a prefixről vesszük (`getQueriesData`) → hibánál MINDEN érintett cache visszaáll.
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { notificationKeys, type NotificationFeed } from '../api';

import { api } from '@/lib/api';

/** A ts-rest a 4xx/5xx-et NEM dobja (a promise `{ status, body }`-vel resolve-ol) → itt dobjuk, hogy fusson a rollback. */
class NotificationHttpError extends Error {
  constructor(readonly status: number) {
    super(`HTTP ${String(status)}`);
    this.name = 'NotificationHttpError';
  }
}

/**
 * „Sikeres" válasz, ami mégis kudarcot jelent (`{ ok: false }`) — DEFENZÍV ág: a BFF ma 502-t ad az
 * upstream-hibára (nincs hamis siker), de ha a szerződés elmozdulna, a hamis siker-toast + a NEM visszagörgetett
 * optimista nullázás a legrosszabb kimenet lenne. Ezért a `ok === false` is hibának számít.
 */
class NotificationNotAppliedError extends Error {
  constructor() {
    super('A művelet nem került alkalmazásra (ok: false).');
    this.name = 'NotificationNotAppliedError';
  }
}

/** Egy nem-alkalmazott (`ok:false`) 200-as válasz felismerése — a body alakja védekezően olvasva. */
function isNotApplied(body: unknown): boolean {
  return typeof body === 'object' && body !== null && (body as { ok?: unknown }).ok === false;
}

/** A prefix-snapshot alakja (query-kulcs → cache-érték) a rollbackhoz. */
type FeedSnapshot = [readonly unknown[], NotificationFeed | undefined][];

function snapshotFeeds(qc: QueryClient): FeedSnapshot {
  return qc.getQueriesData<NotificationFeed>({ queryKey: notificationKeys.feeds() });
}

function restoreFeeds(qc: QueryClient, snapshot: FeedSnapshot | undefined): void {
  for (const [key, value] of snapshot ?? []) qc.setQueryData(key, value);
}

/** Egy sor olvasottra állítása + az olvasatlan-szám csökkentése MINDEN betöltött lapon. */
function markRowRead(feed: NotificationFeed | undefined, id: string): NotificationFeed | undefined {
  if (!feed) return feed;
  // Csak akkor csökkentjük a számlálót, ha a sor VALÓBAN olvasatlan volt (kétszeres kattintás ne vigye mínuszba).
  const wasUnread = feed.pages.some(
    (p) => p.status === 200 && p.body.data.some((r) => r.id === id && r.readAt === null),
  );
  const readAt = new Date().toISOString();
  return {
    ...feed,
    pages: feed.pages.map((page) =>
      page.status === 200
        ? {
            ...page,
            body: {
              ...page.body,
              data: page.body.data.map((r) => (r.id === id ? { ...r, readAt } : r)),
              unreadCount: wasUnread
                ? Math.max(0, page.body.unreadCount - 1)
                : page.body.unreadCount,
            },
          }
        : page,
    ),
  };
}

/** Minden betöltött sor olvasottra állítása + az olvasatlan-szám nullázása. */
function markAllRead(feed: NotificationFeed | undefined): NotificationFeed | undefined {
  if (!feed) return feed;
  const readAt = new Date().toISOString();
  return {
    ...feed,
    pages: feed.pages.map((page) =>
      page.status === 200
        ? {
            ...page,
            body: {
              ...page.body,
              data: page.body.data.map((r) => (r.readAt === null ? { ...r, readAt } : r)),
              unreadCount: 0,
            },
          }
        : page,
    ),
  };
}

/**
 * EGY értesítés olvasottnak jelölése (sor-kattintás). Optimista: a badge/sor AZONNAL frissül; hibára rollback +
 * hiba-toast. Siker esetén NINCS toast (néma művelet — a `messages` olvasottság-mintája).
 */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  return useMutation<unknown, unknown, { id: string }, { snapshot: FeedSnapshot }>({
    mutationFn: async ({ id }) => {
      const res = await api.notificationMarkRead.mutation({ params: { id }, body: {} });
      if (res.status >= 400) throw new NotificationHttpError(res.status);
      if (isNotApplied(res.body)) throw new NotificationNotAppliedError();
      return res;
    },
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: notificationKeys.feeds() });
      const snapshot = snapshotFeeds(qc);
      qc.setQueriesData<NotificationFeed>({ queryKey: notificationKeys.feeds() }, (old) =>
        markRowRead(old, id),
      );
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreFeeds(qc, ctx?.snapshot);
      toast.error(t('toast:notifications.markReadError'));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * „Mind olvasottnak" — EXPLICIT user-akció → siker-toast. Optimista: minden betöltött sor olvasottra vált és a
 * badge eltűnik; hibára rollback + hiba-toast.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const { t } = useTranslation();

  return useMutation<unknown, unknown, void, { snapshot: FeedSnapshot }>({
    mutationFn: async () => {
      const res = await api.notificationsReadAll.mutation({ body: {} });
      if (res.status >= 400) throw new NotificationHttpError(res.status);
      // A degradált BFF-válasz (`200 {ok:false, updated:0}`) NEM siker: rollback + hiba-toast jár rá, nem
      // siker-toast. (A BFF ma 502-t ad — ez a szerződés-drift elleni második fal.)
      if (isNotApplied(res.body)) throw new NotificationNotAppliedError();
      return res;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: notificationKeys.feeds() });
      const snapshot = snapshotFeeds(qc);
      qc.setQueriesData<NotificationFeed>({ queryKey: notificationKeys.feeds() }, (old) =>
        markAllRead(old),
      );
      return { snapshot };
    },
    onError: (_err, _vars, ctx) => {
      restoreFeeds(qc, ctx?.snapshot);
      toast.error(t('toast:notifications.markAllError'));
    },
    onSuccess: () => {
      toast.success(t('toast:notifications.allRead'));
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
